import { FC, Fragment } from 'react';
import { useForm, Controller } from 'react-hook-form';
// Models
import { ICalendarEventType } from 'app/models/CalendarEvent';
// Redux
import { useAppDispatch } from 'app/hooks/useStore';
// Async
import { createCalendarEventType, updateCalendarEventType } from 'app/store/CalendarEventTypes/CalendarEventTypesAsync';
// MUI
import { Box } from '@mui/material';
// Icons
import { Check as CheckIcon } from '@mui/icons-material';
// Components
import Dialog from 'app/components/Dialog';
import { Button, Input } from 'app/components/Mui';
// Utilities
import { isRequired } from 'app/utilities/Validations';
// i18next
import { useTranslation } from 'react-i18next';

interface IFormData {
  name: string;
  color: string;
}

type Props = {
  open: boolean;
  onClose: () => void;
  eventType?: ICalendarEventType;
}

const EventTypesForm:FC<Props> = ({ open, onClose, eventType }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { control, handleSubmit, formState: { errors }, watch, setValue } = useForm<IFormData>({
    defaultValues: {
      name: eventType?.name || '',
      color: eventType?.color || '#039be5',
    }
  });

  const colorWatcher = watch('color');

  const onClick = (color:string) => {
    setValue('color', color);
  }

  const onSubmit = handleSubmit((data:IFormData) => {
    if ( eventType?.id ){
      asyncUpdateEventType({ eventTypeId: eventType.id, data });
    } else {
      asyncCreateEventType(data);
    }
  });

  const asyncCreateEventType = async (data:IFormData) => {
    try {
      await dispatch(createCalendarEventType(data)).unwrap();
      onClose();
    } catch(err){}
  }

  const asyncUpdateEventType = async (data:any) => {
    try {
      await dispatch(updateCalendarEventType(data)).unwrap();
      onClose();
    } catch(err){}
  }

  const actions = (
    <Fragment>
      <Button
        name="Cancel event type dialog"
        onClick={onClose}
      >{t('buttons.close')}</Button>
      <Button
        name={`${eventType ? 'update' : 'create'} event type dialog`}
        onClick={onSubmit}
        variant="contained"
        color="primary"
      >{eventType ? t('buttons.update') : t('buttons.create')}</Button>
    </Fragment>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={eventType ? t('pages.companyCalendar.updateEventTypeFormTitle') : t('pages.companyCalendar.createEventTypeFormTitle')}
      actions={actions}
    >
      <form noValidate onSubmit={onSubmit}>
        {/* Name */}
        <Controller
          control={control} name="name"
          rules={{ required: isRequired }}
          render={({ field }) => (
            <Input
              {...field}
              label={t('labels.name')}
              error={Boolean(errors.name)}
              helperText={errors.name?.message || ''}
              required
            />
          )}
        />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, my: 2 }}>
          {colors.map((color) => (
            <Box
              key={color}
              onClick={() => onClick(color)}
              sx={{
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: color,
                color: 'white',
                overflow: 'hidden',
                cursor: 'pointer'
              }}
            >
              {colorWatcher === color ? <CheckIcon /> : null}
            </Box>
          ))}
        </Box>

        {/* Color */}
        <Controller
          control={control} name="color"
          rules={{ required: isRequired }}
          render={({ field }) => (
            <Input
              {...field}
              label={t('labels.color')} type="color"
            />
          )}
        />
      </form>
    </Dialog>
  )
}

export default EventTypesForm;

const colors = [
  '#C9493A', '#8F37AA', '#6042B0', '#4054AF', '#3E87D4',
  '#4096D4', '#48A3B8', '#419388', '#57914D', '#C29936',
  '#D45831', '#74564A', '#657D8A'
];
